/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import React, { useEffect, useMemo } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Box from "components/generals/Box";
import Typography from "components/generals/Typography";
import { AccountingMovementInterface } from "@malmur-technologies/medicalsystem-shared";
import { useTranslation } from "react-i18next";
import { calculateAccountingTotals } from "utils/accountingMovements.utils";
import { formatPrice } from "utils/number.utils";
import { useSelector } from "react-redux";
import { RootState } from "store/store";

/**
 * Properties for the AccountingMovementsTotals component.
 * 
 * @interface AccountingMovementsTotalsProps
 * @property {AccountingMovementInterface[]} movements - Array of accounting movements to calculate totals from
 */
interface AccountingMovementsTotalsProps {
  movements: AccountingMovementInterface[];
}

/**
 * Accounting movements totals summary card component.
 * Displays calculated balance totals for patient accounting.
 * 
 * Features:
 * - Account status display
 * - Total account balance (overall)
 * - Insurance balance (amount covered by insurance)
 * - Patient balance (patient responsibility)
 * - Formatted currency display (USD)
 * - Automatic recalculation when movements change
 * - Compact card layout
 * - i18n translation support
 * 
 * Calculations:
 * - Uses calculateAccountingTotals utility
 * - Balance: Total amount due
 * - Balance Insurance: Amount insurance is responsible for
 * - Balance Patient: Amount patient must pay
 * 
 * Display Format:
 * - All amounts shown as: "$ X.XX USD"
 * - Two decimal precision
 * - Status from first movement's accounting object
 * 
 * @param {AccountingMovementsTotalsProps} props - Component properties
 * @param {AccountingMovementInterface[]} props.movements - Movements to summarize
 * 
 * @returns {JSX.Element} Totals summary card
 */
const AccountingMovementsTotals: React.FC<AccountingMovementsTotalsProps> = ({ movements }) => {

  const { t } = useTranslation();
  const generalSettings = useSelector((state: RootState) => state.generalSettings);
  const isInsuranceModuleEnabled = generalSettings?.isInsuranceModuleEnabled ?? true;

  const {
    balance,
    balanceInsurance,
    balancePatient,
  } = useMemo(() => calculateAccountingTotals(movements), [movements]);

  const status = useMemo(() => {
    return movements && movements.length > 0 ? movements[0].accounting?.status || "" : "";
  }, [movements]);

  return (
    <Card id="accounting-movements-totals" className="card-base">
      <Box p={2}>
        <Grid container whiteSpace="nowrap">
          <Grid item xs={12}>
            <Typography variant="caption" color="text" className="balanceText">

              <strong>
                {t("AccountingMovementsTotals.status")}: {status ? t(`AccountingMovementsTotals.${status}`) : t("AccountingMovementsTotals.noMovementsStatus")}
              </strong>
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text" className="balanceText">
              <strong>
                {t("AccountingMovementsTotals.accountBalance")}:{" "}
                {formatPrice(balance)}
              </strong>
            </Typography>
          </Grid>
          {isInsuranceModuleEnabled && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text" className="balanceText">
                <strong>
                  {t("AccountingMovementsTotals.insuranceBalance")}:{" "}
                  {formatPrice(balanceInsurance)}
                </strong>
              </Typography>
            </Grid>
          )}
          <Grid item xs={12}>
            <Typography variant="caption" color="text" className="balanceText">
              <strong>
                {t("AccountingMovementsTotals.patientBalance")}: {formatPrice(balancePatient)}
              </strong>
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Card>
  );
};

export default AccountingMovementsTotals;
